import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Button,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SummarizeIcon from "@mui/icons-material/Summarize";
import axios from "../../../../axios-order";
import BoxWrapper from "../../../../components/ui/boxWrapper";
import { useLocation } from "react-router-dom";
import { canRead, canWrite } from "../../../../utils/privilegeUtils";

function OutwardSummaryDetails() {
  const location = useLocation();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryDetails, setSummaryDetails] = useState(null);
  const [config, setConfig] = useState(null);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const downloadBase64File = (contentType, base64Data, fileName) => {
    const linkSource = `data:${contentType};base64,${base64Data}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  };

  const handleExportExcel = () => {
    setIsExportingExcel(true);
    if (reportData) {
      axios.get(`/gstr9_comparison_report/${reportData}`)
        .then((response) => {
          const contentType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
          const base64Data = response.data.file;
          if (base64Data) {
            const filename = `${summaryDetails?.comparison_type2 || 'OutwardComparison'}.xlsx`;
            downloadBase64File(contentType, base64Data, filename);
          } else {
            console.error("Base64 data is missing in the response");
          }
        })
        .catch((error) => {
          console.log(error);
        }).finally(() => {
          setIsExportingExcel(false);
        });
    }
  };

  useEffect(() => {
    const item = location.state?.reportId || null;
    setReportData(item);
    console.log("Passed item:", item);
  }, [location]);

  useEffect(() => {
    if (reportData) {
      setLoading(true);
      axios
        .get(`/gstr9_comparison_report_data/${reportData}`)
        .then((response) => {
          setSummaryDetails(response.data.data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching API data:", err);
          setError("Failed to fetch data");
          setLoading(false);
        });
    }
  }, [reportData]);

  const getComparisonConfig = () => {
    if (!summaryDetails || !summaryDetails.comparison_data) return null;

    const type2 = summaryDetails.comparison_type2;

    if (type2 === "Books vs GSTR1 vs GSTR3B") {
      return {
        dataKey: 'books_vs_gstr1_data',
        leftLabel: 'AS PER BOOKS',
        middleLabel: 'AS PER GSTR-1',
        rightLabel: 'AS PER GSTR-3B',
        isNested: true,
        isTriple: true,
        getMonthData: (month) => {
          const bk_g1 = summaryDetails.comparison_data.books_vs_gstr1_data?.[month];
          const bk_g3b = summaryDetails.comparison_data.books_vs_gstr3b_data?.[month];
          const g1_g3b = summaryDetails.comparison_data.gstr1_vs_gstr3b_data?.[month];
          return {
            left: {
              taxable_value: bk_g1?.asperbooks?.cws_taxable_value ?? null,
              igst: bk_g1?.asperbooks?.cws_igst ?? null,
              cgst: bk_g1?.asperbooks?.cws_cgst ?? null,
              sgst: bk_g1?.asperbooks?.cws_sgst ?? null,
              cess: bk_g1?.asperbooks?.cws_cess ?? null,
              total: bk_g1?.asperbooks?.cws_total_amount ?? null,
            },
            middle: {
              taxable_value: bk_g1?.aspergst?.gstr_taxable_value ?? null,
              igst: bk_g1?.aspergst?.gstr_igst ?? null,
              cgst: bk_g1?.aspergst?.gstr_cgst ?? null,
              sgst: bk_g1?.aspergst?.gstr_sgst ?? null,
              cess: bk_g1?.aspergst?.gstr_cess ?? null,
              total: bk_g1?.aspergst?.gstr_total_amount ?? null,
            },
            right: {
              taxable_value: bk_g3b?.gstr3b_total_taxable ?? null,
              igst: bk_g3b?.gstr3b_integrated_tax ?? null,
              cgst: bk_g3b?.gstr3b_central_tax ?? null,
              sgst: bk_g3b?.gstr3b_state_or_ut_tax ?? null,
              cess: bk_g3b?.gstr3b_cess ?? null,
              total: bk_g3b?.gstr3b_total_amount ?? null,
            },
            diff_books_gstr1: {
              taxable_value: bk_g1?.difference?.taxable_value_diff ?? null,
              igst: bk_g1?.difference?.igst_diff ?? null,
              cgst: bk_g1?.difference?.cgst_diff ?? null,
              sgst: bk_g1?.difference?.sgst_diff ?? null,
              cess: bk_g1?.difference?.cess_diff ?? null,
              total: bk_g1?.difference?.total_amount_diff ?? null,
            },
            diff_books_gstr3b: {
              taxable_value: bk_g3b?.diff_total_taxable ?? null,
              igst: bk_g3b?.diff_integrated_tax ?? null,
              cgst: bk_g3b?.diff_central_tax ?? null,
              sgst: bk_g3b?.diff_state_or_ut_tax ?? null,
              cess: bk_g3b?.diff_cess ?? null,
              total: bk_g3b?.diff_total_amount ?? null,
            },
            diff_gstr1_gstr3b: {
              taxable_value: g1_g3b?.diff_total_taxable ?? null,
              igst: g1_g3b?.diff_integrated_tax ?? null,
              cgst: g1_g3b?.diff_central_tax ?? null,
              sgst: g1_g3b?.diff_state_or_ut_tax ?? null,
              cess: g1_g3b?.diff_cess ?? null,
              total: g1_g3b?.diff_total_amount ?? null,
            },
            difference: {
              taxable_value: g1_g3b?.diff_total_taxable ?? null,
              igst: g1_g3b?.diff_integrated_tax ?? null,
              cgst: g1_g3b?.diff_central_tax ?? null,
              sgst: g1_g3b?.diff_state_or_ut_tax ?? null,
              cess: g1_g3b?.diff_cess ?? null,
              total: g1_g3b?.diff_total_amount ?? null,
            }
          };
        }
      };
    }

    if (type2 === "Books vs GSTR1") {
      return {
        dataKey: 'books_vs_gstr1_data',
        leftLabel: 'AS PER BOOKS',
        rightLabel: 'AS PER GSTR1',
        isNested: true,
        getMonthData: (month) => {
          const data = summaryDetails.comparison_data.books_vs_gstr1_data[month];
          return {
            left: {
              taxable_value: data?.asperbooks?.cws_taxable_value,
              igst: data?.asperbooks?.cws_igst,
              cgst: data?.asperbooks?.cws_cgst,
              sgst: data?.asperbooks?.cws_sgst,
              cess: data?.asperbooks?.cws_cess,
              total: data?.asperbooks?.cws_total_amount
            },
            right: {
              taxable_value: data?.aspergst?.gstr_taxable_value,
              igst: data?.aspergst?.gstr_igst,
              cgst: data?.aspergst?.gstr_cgst,
              sgst: data?.aspergst?.gstr_sgst,
              cess: data?.aspergst?.gstr_cess,
              total: data?.aspergst?.gstr_total_amount
            },
            difference: {
              taxable_value: data?.difference?.taxable_value_diff,
              igst: data?.difference?.igst_diff,
              cgst: data?.difference?.cgst_diff,
              sgst: data?.difference?.sgst_diff,
              cess: data?.difference?.cess_diff,
              total: data?.difference?.total_amount_diff
            }
          };
        }
      };
    }

    if (type2 === "Books vs GSTR3B") {
      return {
        dataKey: 'books_vs_gstr3b_data',
        leftLabel: 'AS PER BOOKS',
        rightLabel: 'AS PER GSTR3B',
        isNested: true,
        getMonthData: (month) => {
          const data = summaryDetails.comparison_data.books_vs_gstr3b_data[month];
          return {
            left: {
              taxable_value: data?.book_taxable_value,
              igst: data?.book_igst,
              cgst: data?.book_cgst,
              sgst: data?.book_sgst,
              cess: data?.book_cess,
              total: data?.book_total_amount
            },
            right: {
              taxable_value: data?.gstr3b_total_taxable,
              igst: data?.gstr3b_integrated_tax,
              cgst: data?.gstr3b_central_tax,
              sgst: data?.gstr3b_state_or_ut_tax,
              cess: data?.gstr3b_cess,
              total: data?.gstr3b_total_amount
            },
            difference: {
              taxable_value: data?.diff_total_taxable,
              igst: data?.diff_integrated_tax,
              cgst: data?.diff_central_tax,
              sgst: data?.diff_state_or_ut_tax,
              cess: data?.diff_cess,
              total: data?.diff_total_amount
            }
          };
        }
      };
    }

    if (type2 === "GSTR1 vs GSTR3B") {
      return {
        dataKey: null,
        leftLabel: 'AS PER GSTR1',
        rightLabel: 'AS PER GSTR3B',
        isNested: false,
        getMonthData: (month) => {
          const data = summaryDetails.comparison_data[month];
          return {
            left: {
              taxable_value: data?.gstr1_taxable_value,
              igst: data?.gstr1_igst,
              cgst: data?.gstr1_cgst,
              sgst: data?.gstr1_sgst,
              cess: data?.gstr1_cess,
              total: data?.gstr1_total_amount
            },
            right: {
              taxable_value: data?.gstr3b_total_taxable,
              igst: data?.gstr3b_integrated_tax,
              cgst: data?.gstr3b_central_tax,
              sgst: data?.gstr3b_state_or_ut_tax,
              cess: data?.gstr3b_cess,
              total: data?.gstr3b_total_amount
            },
            difference: {
              taxable_value: data?.diff_total_taxable,
              igst: data?.diff_integrated_tax,
              cgst: data?.diff_central_tax,
              sgst: data?.diff_state_or_ut_tax,
              cess: data?.diff_cess,
              total: data?.diff_total_amount
            }
          };
        }
      };
    }

    return null;
  };

  useEffect(() => {
    if (summaryDetails) {
      const calculatedConfig = getComparisonConfig();
      setConfig(calculatedConfig);
    }
  }, [summaryDetails]);

  const numberFormat = (value) => {
    if (value === null || value === undefined || value === "") {
      return "₹ 0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(value);
  };

  const cellCenter = {
    border: "1px solid #c1c1c1",
    padding: "8px",
    textAlign: "center",
    whiteSpace: "nowrap",
  };

  const cellRight = {
    border: "1px solid #c1c1c1",
    padding: "8px",
    textAlign: "right",
    whiteSpace: "nowrap",
  };

  const handleBack = () => {
    window.history.back();
  };

  const getMonths = () => {
    if (!summaryDetails || !summaryDetails.comparison_data) return [];

    if (config?.dataKey) {
      return Object.keys(summaryDetails.comparison_data[config.dataKey] || {});
    } else {
      return Object.keys(summaryDetails.comparison_data);
    }
  };

  const months = getMonths();

  const getNilExemptMonths = () => {
    if (!summaryDetails || !summaryDetails.comparison_data || !summaryDetails.comparison_data.nill_exempt_non_gst_data) {
      return [];
    }
    return Object.keys(summaryDetails.comparison_data.nill_exempt_non_gst_data);
  };

  const nilExemptMonths = getNilExemptMonths();
  const hasNilExemptData = nilExemptMonths.length > 0;

  return (
    <Container maxWidth={false}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "2%",
          padding: "0 2%",
        }}
      >
        <button
          className="button-outlined-primary"
          style={{ marginLeft: "0px", width: "auto", padding: "6px 12px", marginTop: "1px", fontSize: "14px" }}
          onClick={handleBack}
        >
          BACK
        </button>
        <div
          style={{
            flex: "1",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginLeft: "14%",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", textAlign: "center" }}
          >
            OUTWARD COMPARISON REPORT{" "}
            <SummarizeIcon style={{ marginBottom: "4px", color: "#043010" }} />
          </Typography>
          {summaryDetails && (
            <Typography variant="body2" sx={{ textAlign: "center", marginTop: "5px" }}>
              {summaryDetails.comparison_type2} | {summaryDetails.financial_year} | {summaryDetails.from_month} - {summaryDetails.to_month}
            </Typography>
          )}
        </div>
        <Button
          onClick={handleExportExcel}
          variant="contained"
          color="primary"
          style={{ marginLeft: '2%' }}
          disabled={isExportingExcel}
          endIcon={isExportingExcel ? (
            <span
              style={{
                width: "12px",
                height: "12px",
                border: "2px solid currentColor",
                borderTopColor: "transparent",
                borderRadius: "50%",
                display: "inline-block",
                animation: "spin 0.7s linear infinite",
                flexShrink: 0,
              }}
            />
          ) : (
            <FileDownloadIcon />
          )}
        >
          {isExportingExcel ? "Exporting..." : "Export"}
        </Button>
      </div>

      <BoxWrapper
        style={{ width: "100%", maxWidth: "100%" }}
        boxTitle={summaryDetails?.comparison_type2 || "Outward Comparison Report"}
      >
        {loading ? (
          <div
            style={{
              color: "black",
              textAlign: "center",
              fontWeight: "bold",
              padding: "20px",
              fontSize: "16px",
            }}
          >
            Loading… Please wait
          </div>
        ) : error ? (
          <p style={{ color: "red", textAlign: "center" }}>{error}</p>
        ) : !summaryDetails || !summaryDetails.comparison_data || !config ? (
          <div
            style={{
              color: "red",
              textAlign: "center",
              fontSize: "16px",
              padding: "20px",
            }}
          >
            No data found!
          </div>
        ) : (
          <>
            <div style={{ maxHeight: "600px", overflowY: "auto", marginBottom: hasNilExemptData ? "20px" : "0" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: "Arial, sans-serif",
                  fontSize: "13px",
                  tableLayout: "auto",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#d6e3f3", position: "sticky", top: 0, zIndex: 2 }}>
                    <th
                      style={{
                        border: "1px solid #3b6ea5",
                        padding: "8px",
                        textAlign: "center",
                        fontWeight: "bold",
                        color: "#002060",
                        whiteSpace: "nowrap",
                        background: "#d6e3f3",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      Month
                    </th>
                    <th colSpan={6} style={{ border: "1px solid #3b6ea5", padding: "8px", textAlign: "center", fontWeight: "bold", backgroundColor: "#3065d1", color: "white", position: "sticky", top: 0, zIndex: 3 }}>
                      {config.leftLabel}
                    </th>
                    {config.isTriple && (
                      <th colSpan={6} style={{ border: "1px solid #3b6ea5", padding: "8px", textAlign: "center", fontWeight: "bold", backgroundColor: "#7b2fd4", color: "white", position: "sticky", top: 0, zIndex: 3 }}>
                        {config.middleLabel}
                      </th>
                    )}
                    <th colSpan={6} style={{ border: "1px solid #3b6ea5", padding: "8px", textAlign: "center", fontWeight: "bold", backgroundColor: "#1ed457", color: "white", position: "sticky", top: 0, zIndex: 3 }}>
                      {config.rightLabel}
                    </th>
                    {config.isTriple ? (
                      <>
                        <th colSpan={6} style={{ border: "1px solid #3b6ea5", padding: "8px", textAlign: "center", fontWeight: "bold", backgroundColor: "#d6712d", color: "white", position: "sticky", top: 0, zIndex: 3 }}>
                          DIFFERENCE (BOOKS VS GSTR-1)
                        </th>
                        <th colSpan={6} style={{ border: "1px solid #3b6ea5", padding: "8px", textAlign: "center", fontWeight: "bold", backgroundColor: "#d6712d", color: "white", position: "sticky", top: 0, zIndex: 3 }}>
                          DIFFERENCE (BOOKS VS GSTR-3B)
                        </th>
                        <th colSpan={6} style={{ border: "1px solid #3b6ea5", padding: "8px", textAlign: "center", fontWeight: "bold", backgroundColor: "#d6712d", color: "white", position: "sticky", top: 0, zIndex: 3 }}>
                          DIFFERENCE (GSTR-1 VS GSTR-3B)
                        </th>
                      </>
                    ) : (
                      <th colSpan={6} style={{ border: "1px solid #3b6ea5", padding: "8px", textAlign: "center", fontWeight: "bold", backgroundColor: "#d6712d", color: "white", position: "sticky", top: 0, zIndex: 3 }}>
                        DIFFERENCE
                      </th>
                    )}
                  </tr>
                  <tr style={{ backgroundColor: "#e8f0f8", position: "sticky", top: "42px", zIndex: 2 }}>
                    <th style={{ border: "1px solid #3b6ea5", padding: "8px", textAlign: "center", fontWeight: "bold", color: "#002060", whiteSpace: "nowrap", background: "#e8f0f8" }}>
                      Period
                    </th>
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Taxable Value</th>
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Integrated Tax</th>
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Central Tax</th>
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>State Tax</th>
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>CESS</th>
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                    {config.isTriple && (
                      <>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Taxable Value</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Integrated Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Central Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>State Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>CESS</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                      </>
                    )}
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Taxable Value</th>
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Integrated Tax</th>
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Central Tax</th>
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>State Tax</th>
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>CESS</th>
                    <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                    {config.isTriple ? (
                      <>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Taxable Value</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Integrated Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Central Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>State Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>CESS</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>

                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Taxable Value</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Integrated Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Central Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>State Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>CESS</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>

                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Taxable Value</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Integrated Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Central Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>State Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>CESS</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                      </>
                    ) : (
                      <>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Taxable Value</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Integrated Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Central Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>State Tax</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>CESS</th>
                        <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {months.map((month, index) => {
                    const data = config.getMonthData(month);
                    return (
                      <tr key={index}>
                        <td style={cellCenter}>{month}</td>

                        <td style={cellRight}>{numberFormat(data.left.taxable_value)}</td>
                        <td style={cellRight}>{numberFormat(data.left.igst)}</td>
                        <td style={cellRight}>{numberFormat(data.left.cgst)}</td>
                        <td style={cellRight}>{numberFormat(data.left.sgst)}</td>
                        <td style={cellRight}>{numberFormat(data.left.cess)}</td>
                        <td style={cellRight}>{numberFormat(data.left.total)}</td>

                        {config.isTriple && (
                          <>
                            <td style={cellRight}>{numberFormat(data.middle.taxable_value)}</td>
                            <td style={cellRight}>{numberFormat(data.middle.igst)}</td>
                            <td style={cellRight}>{numberFormat(data.middle.cgst)}</td>
                            <td style={cellRight}>{numberFormat(data.middle.sgst)}</td>
                            <td style={cellRight}>{numberFormat(data.middle.cess)}</td>
                            <td style={cellRight}>{numberFormat(data.middle.total)}</td>
                          </>
                        )}

                        <td style={cellRight}>{numberFormat(data.right.taxable_value)}</td>
                        <td style={cellRight}>{numberFormat(data.right.igst)}</td>
                        <td style={cellRight}>{numberFormat(data.right.cgst)}</td>
                        <td style={cellRight}>{numberFormat(data.right.sgst)}</td>
                        <td style={cellRight}>{numberFormat(data.right.cess)}</td>
                        <td style={cellRight}>{numberFormat(data.right.total)}</td>

                        {config.isTriple ? (
                          <>

                            <td style={cellRight}>{numberFormat(data.diff_books_gstr1.taxable_value)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_books_gstr1.igst)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_books_gstr1.cgst)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_books_gstr1.sgst)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_books_gstr1.cess)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_books_gstr1.total)}</td>

                            <td style={cellRight}>{numberFormat(data.diff_books_gstr3b.taxable_value)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_books_gstr3b.igst)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_books_gstr3b.cgst)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_books_gstr3b.sgst)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_books_gstr3b.cess)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_books_gstr3b.total)}</td>

                            <td style={cellRight}>{numberFormat(data.diff_gstr1_gstr3b.taxable_value)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_gstr1_gstr3b.igst)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_gstr1_gstr3b.cgst)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_gstr1_gstr3b.sgst)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_gstr1_gstr3b.cess)}</td>
                            <td style={cellRight}>{numberFormat(data.diff_gstr1_gstr3b.total)}</td>
                          </>
                        ) : (
                          <>
                            <td style={cellRight}>{numberFormat(data.difference.taxable_value)}</td>
                            <td style={cellRight}>{numberFormat(data.difference.igst)}</td>
                            <td style={cellRight}>{numberFormat(data.difference.cgst)}</td>
                            <td style={cellRight}>{numberFormat(data.difference.sgst)}</td>
                            <td style={cellRight}>{numberFormat(data.difference.cess)}</td>
                            <td style={cellRight}>{numberFormat(data.difference.total)}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasNilExemptData && (
              <div style={{ maxHeight: "400px", overflowY: "auto", marginTop: "30px" }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", marginBottom: "10px" }}
                >
                  Nil, Exempt & Non-GST Comparison
                </Typography>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontFamily: "Arial, sans-serif",
                    fontSize: "13px",
                    tableLayout: "auto",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#d6e3f3" }}>
                      <th
                        rowSpan={2}
                        style={{
                          border: "1px solid #3b6ea5",
                          padding: "8px",
                          textAlign: "center",
                          fontWeight: "bold",
                          color: "#002060",
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        }}
                      >
                        MONTH
                      </th>

                      <th
                        colSpan={config.isTriple ? 4 : (config.dataKey === 'books_vs_gstr1_data' ? 4 : 3)}
                        style={{
                          border: "1px solid #3b6ea5",
                          padding: "8px",
                          textAlign: "center",
                          fontWeight: "bold",
                          backgroundColor: "#d6e8d4",
                          color: "#002060",
                        }}
                      >
                        Total Value - As Per Books
                      </th>

                      <th
                        colSpan={config.isTriple ? 4 : (config.dataKey === 'books_vs_gstr1_data' ? 4 : 3)}
                        style={{
                          border: "1px solid #3b6ea5",
                          padding: "8px",
                          textAlign: "center",
                          fontWeight: "bold",
                          backgroundColor: "#fff9cc",
                          color: "#002060",
                        }}
                      >
                        Total Value - As Per {config.dataKey === 'books_vs_gstr1_data' ? 'GSTR-1' : config.isTriple ? 'GSTR-1' : 'GSTR-3B'}
                      </th>
                      {config.isTriple && (
                        <th
                          colSpan={3}
                          style={{
                            border: "1px solid #3b6ea5",
                            padding: "8px",
                            textAlign: "center",
                            fontWeight: "bold",
                            backgroundColor: "#d4edda",
                            color: "#002060",
                          }}
                        >
                          Total Value - As Per GSTR-3B
                        </th>
                      )}
                      {config.isTriple ? (
                        <>
                          <th
                            colSpan={4}
                            style={{
                              border: "1px solid #3b6ea5",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "bold",
                              backgroundColor: "#d6712d",
                              color: "white",
                            }}
                          >
                            Difference (Books vs GSTR-1)
                          </th>
                          <th
                            colSpan={4}
                            style={{
                              border: "1px solid #3b6ea5",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "bold",
                              backgroundColor: "#d6712d",
                              color: "white",
                            }}
                          >
                            Difference (Books vs GSTR-3B)
                          </th>
                          <th
                            colSpan={4}
                            style={{
                              border: "1px solid #3b6ea5",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "bold",
                              backgroundColor: "#d6712d",
                              color: "white",
                            }}
                          >
                            Difference (GSTR-1 vs GSTR-3B)
                          </th>
                        </>
                      ) : (
                        <th
                          colSpan={config.dataKey === 'books_vs_gstr1_data' ? 4 : 3}
                          style={{
                            border: "1px solid #3b6ea5",
                            padding: "8px",
                            textAlign: "center",
                            fontWeight: "bold",
                            backgroundColor: "#d6712d",
                            color: "#002060",
                          }}
                        >
                          Difference
                        </th>
                      )}
                    </tr>
                    <tr style={{ backgroundColor: "#e8f0f8" }}>
                      {/* As Per Books sub-columns */}
                      {config.isTriple ? (
                        <>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                        </>
                      ) : config.dataKey === 'books_vs_gstr1_data' ? (
                        <>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                        </>
                      ) : (
                        <>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil & Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                        </>
                      )}

                      {config.isTriple ? (
                        <>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                        </>
                      ) : config.dataKey === 'books_vs_gstr1_data' ? (
                        <>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                        </>
                      ) : (
                        <>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil & Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                        </>
                      )}

                      {config.isTriple && (
                        <>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil & Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                        </>
                      )}

                      {config.isTriple ? (
                        <>

                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>

                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil & Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>

                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil & Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>

                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}></th>
                        </>
                      ) : config.dataKey === 'books_vs_gstr1_data' ? (
                        <>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                        </>
                      ) : (
                        <>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Nil & Exempt</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Non-GST</th>
                          <th style={{ ...cellCenter, background: "#e8f0f8", fontWeight: "bold" }}>Total Amount</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {nilExemptMonths.map((month, index) => {
                      const monthData = summaryDetails.comparison_data.nill_exempt_non_gst_data[month];
                      const monthDataGstr3b = summaryDetails.comparison_data.nill_exempt_non_gst_gstr3b_data?.[month];

                      return (
                        <tr key={index}>
                          <td style={cellCenter}>{month}</td>

                          {config.isTriple ? (
                            <>
                              <td style={cellRight}>{numberFormat(monthData?.asperbooks?.nil || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.asperbooks?.exempted || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.asperbooks?.non_gst || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.asperbooks?.total || 0)}</td>
                            </>
                          ) : config.dataKey === 'books_vs_gstr1_data' ? (
                            <>
                              <td style={cellRight}>{numberFormat(monthData?.asperbooks?.nil || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.asperbooks?.exempted || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.asperbooks?.non_gst || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.asperbooks?.total || 0)}</td>
                            </>
                          ) : (
                            <>
                              <td style={cellRight}>{numberFormat(monthData?.asperbooks?.nil_and_exempt || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.asperbooks?.non_gst || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.asperbooks?.total || 0)}</td>
                            </>
                          )}

                          {config.isTriple ? (
                            <>
                              <td style={cellRight}>{numberFormat(monthData?.aspergst?.nil || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.aspergst?.exempted || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.aspergst?.non_gst || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.aspergst?.total || 0)}</td>
                            </>
                          ) : config.dataKey === 'books_vs_gstr1_data' ? (
                            <>
                              <td style={cellRight}>{numberFormat(monthData?.aspergst?.nil || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.aspergst?.exempted || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.aspergst?.non_gst || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.aspergst?.total || 0)}</td>
                            </>
                          ) : (
                            <>
                              <td style={cellRight}>{numberFormat(monthData?.aspergst?.nil_and_exempt || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.aspergst?.non_gst || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.aspergst?.total || 0)}</td>
                            </>
                          )}

                          {config.isTriple && (
                            <>
                              <td style={cellRight}>{numberFormat(monthDataGstr3b?.aspergst?.nil_and_exempt || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthDataGstr3b?.aspergst?.non_gst || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthDataGstr3b?.aspergst?.total || 0)}</td>
                            </>
                          )}

                          {config.isTriple ? (
                            <>

                              <td style={cellRight}>{numberFormat(monthData?.difference?.nil || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.difference?.exempted || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.difference?.non_gst || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.difference?.total || 0)}</td>

                              <td style={cellRight}>{numberFormat(monthDataGstr3b?.difference?.nil_and_exempt || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthDataGstr3b?.difference?.non_gst || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthDataGstr3b?.difference?.total || 0)}</td>

                              <td style={cellRight}>{numberFormat(((monthDataGstr3b?.difference?.nil_and_exempt || 0) - ((monthData?.difference?.nil || 0) + (monthData?.difference?.exempted || 0))))}</td>
                              <td style={cellRight}>{numberFormat(((monthDataGstr3b?.difference?.non_gst || 0) - (monthData?.difference?.non_gst || 0)))}</td>
                              <td style={cellRight}>{numberFormat(((monthDataGstr3b?.difference?.total || 0) - (monthData?.difference?.total || 0)))}</td>
                              <td style={cellRight}></td>
                            </>
                          ) : config.dataKey === 'books_vs_gstr1_data' ? (
                            <>
                              <td style={cellRight}>{numberFormat(monthData?.difference?.nil || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.difference?.exempted || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.difference?.non_gst || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.difference?.total || 0)}</td>
                            </>
                          ) : (
                            <>
                              <td style={cellRight}>{numberFormat(monthData?.difference?.nil_and_exempt || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.difference?.non_gst || 0)}</td>
                              <td style={cellRight}>{numberFormat(monthData?.difference?.total || 0)}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </BoxWrapper>
    </Container>
  );
}

export default OutwardSummaryDetails;